import { BookOpen, ExternalLink } from "lucide-react";
import { useLang } from "../i18n/LangContext";

// Κατάλογος εγγραφών βιβλιογραφίας με προαιρετικό σύνδεσμο
const BiblioList = ({ items, prefix, linkLabel }) => (
  <ul className="space-y-4">
    {items.map((b, i) => (
      <li
        key={i}
        data-testid={`${prefix}-item-${i}`}
        className="border-l-2 border-line pl-5 text-sm md:text-base leading-relaxed text-foreground/85"
      >
        {b.text}
        {b.url && (
          <a
            href={b.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${prefix}-link-${i}`}
            className="mt-2 flex items-center justify-end gap-1.5 text-sm font-semibold text-aegean transition-colors hover:text-terra"
          >
            {linkLabel}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        )}
      </li>
    ))}
  </ul>
);

export const Bibliography = () => {
  const { d, ui } = useLang();
  return (
    <section id="bibliography" data-testid="bibliography-section" className="scroll-mt-24 mt-24 border-t border-line pt-16">
      <div className="mb-8 flex items-center gap-4">
        <div className="rounded-full bg-aegean p-3 text-paper">
          <BookOpen className="h-5 w-5" />
        </div>
        <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-ink">{ui.biblioTitle}</h2>
      </div>
      <h3 data-testid="biblio-classic-title" className="mt-4 font-serif text-lg md:text-xl font-semibold text-ink">{ui.biblioClassicTitle}</h3>
      <div className="mt-4">
        <BiblioList items={d.BIBLIOGRAPHY_CLASSIC} prefix="biblio-classic" linkLabel={ui.pubsLinkLabel} />
      </div>
      <h3 data-testid="biblio-recent-title" className="mt-10 font-serif text-lg md:text-xl font-semibold text-ink">{ui.biblioRecentTitle}</h3>
      <div className="mt-4">
        <BiblioList items={d.BIBLIOGRAPHY_RECENT} prefix="biblio-recent" linkLabel={ui.pubsLinkLabel} />
      </div>
    </section>
  );
};

export const AboutSection = () => {
  const { ui } = useLang();
  return (
    <section id="about" data-testid="about-section" className="scroll-mt-24 border-t border-line bg-surface/50 pb-20 pt-10 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <p className="eyebrow">{ui.aboutEyebrow}</p>
        <h2 className="mt-3 font-serif text-3xl md:text-4xl font-semibold tracking-tight text-ink">
          {ui.aboutTitle}
        </h2>
        <div className="mt-10 grid gap-10 md:grid-cols-2">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-terra">{ui.aboutMethodTitle}</h3>
            <ul className="mt-4 space-y-3 text-sm md:text-base leading-relaxed text-foreground/85" data-testid="about-methodology">
              {ui.aboutMethodology.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-terra">{ui.aboutCreditsTitle}</h3>
            <ul className="mt-4 space-y-3 text-sm md:text-base leading-relaxed text-foreground/85" data-testid="about-credits">
              <li>{ui.aboutCreditAuthor} <strong>{ui.authorName}</strong></li>
              <li>{ui.aboutCreditSponsor} <a href="https://novuscodex.gr" target="_blank" rel="noopener noreferrer" className="font-semibold text-aegean underline-offset-4 hover:text-terra hover:underline" data-testid="about-sponsor-link">NOVUS CODEX</a></li>
              <li>{ui.aboutCreditAi}</li>
              <li>{ui.aboutCreditMusic}</li>
              <li>{ui.aboutCopyright}</li>
            </ul>
          </div>
        </div>
        <div data-testid="about-dedication" className="mt-16 border-t border-line pt-10 text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-terra">{ui.aboutDedication}</p>
          <p className="mt-4 font-serif text-xl italic leading-relaxed text-ink md:text-2xl">{ui.aboutDedicationText}</p>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { ui } = useLang();
  return (
    <footer data-testid="footer" className="relative mt-24 overflow-hidden bg-anthra">
      <div className="relative z-10 mx-auto max-w-6xl px-6 py-14">
        <div data-testid="footer-credit" className="flex flex-col items-center gap-4 text-center">
          <a href="https://novuscodex.gr" target="_blank" rel="noopener noreferrer" data-testid="novus-logo-footer-link" aria-label="NOVUS CODEX — sponsor website">
            <img src="/images/novus-codex-logo-white.png" alt="NOVUS CODEX" className="h-12 w-auto transition-transform duration-200 hover:scale-105" data-testid="novus-logo-footer" />
          </a>
          <p className="max-w-lg text-xs leading-relaxed text-paper/70">
            {ui.footerCredit}
            <br />
            <span data-testid="footer-credit-author">{ui.footerCreditAuthor}</span>
          </p>
        </div>
        <p data-testid="footer-copyright" className="mt-4 text-center text-[11px] tracking-wide text-paper/50">
          {ui.footerCopyright}
        </p>
      </div>
    </footer>
  );
};
